/**
 * Aktionen am Antrag (ADR 011): der nächste Schritt als Knopf, die übrigen Aktionen im Menü
 * daneben, und der Dialog dazu – aufgebaut wie `useEvidenceAktionen` am Nachweis.
 *
 * Was zulässig ist, liefert der Server je Antrag (`PermitProcedure.actions`, der nächste Schritt
 * zuerst) – hier wird nichts davon nachgebaut.
 */
import React, { useCallback, useState } from "react";
import {
  ArrowForward as StatusChangeIcon,
  BlockOutlined as WithdrawIcon,
  MoreTimeOutlined as ExtendIcon,
  RestoreOutlined as UndoWithdrawIcon,
} from "@mui/icons-material";
import AntragUebergangDialog from "./AntragUebergangDialog";
import {
  PermitProcedure,
  ProcedureAction,
  ProcedureTransition,
} from "../../../../types";
import {
  ActionsMenuItem,
  ActionsMenuList,
} from "../../../common/overlays/ActionsMenu";

/** Aktionen, die keinen Schritt im Ablauf nach vorn sind – im Menü nach dem Trennstrich */
const SIDE_ACTIONS = [
  "request_resubmission",
  "record_appeal",
  "extend_deadline",
  "reset",
  "withdraw",
  "undo_withdrawal",
];

const isSideAction = (action: ProcedureAction): boolean =>
  !action.target_status || SIDE_ACTIONS.includes(action.action);

const actionIcon = (action: ProcedureAction): React.ReactElement => {
  switch (action.action) {
    case "withdraw":
      return <WithdrawIcon fontSize="small" />;
    case "undo_withdrawal":
      return <UndoWithdrawIcon fontSize="small" />;
    case "extend_deadline":
      return <ExtendIcon fontSize="small" />;
    default:
      return <StatusChangeIcon fontSize="small" />;
  }
};

/** Die Aktion für den Knopf: beim zurückgenommenen Antrag das Aufheben, sonst der nächste Schritt */
export const mainAction = (
  procedure: PermitProcedure,
): ProcedureAction | null => {
  if (procedure.withdrawn) {
    return (
      procedure.actions.find((a) => a.action === "undo_withdrawal") ?? null
    );
  }
  const first = procedure.actions[0];
  return first && !isSideAction(first) ? first : null;
};

export function useProcedureActions(
  onTransition: (
    procedureId: string,
    data: ProcedureTransition,
  ) => Promise<PermitProcedure | null>,
) {
  const [menu, setMenu] = useState<{
    procedure: PermitProcedure;
    anchor: HTMLElement;
  } | null>(null);
  const [dialog, setDialog] = useState<{
    procedure: PermitProcedure;
    action: ProcedureAction;
  } | null>(null);

  const openMenu = useCallback(
    (procedure: PermitProcedure, anchor: HTMLElement) =>
      setMenu({ procedure, anchor }),
    [],
  );

  const openAction = useCallback(
    (procedure: PermitProcedure, action: ProcedureAction) => {
      setMenu(null);
      setDialog({ procedure, action });
    },
    [],
  );

  const handleSubmit = useCallback(
    async (data: ProcedureTransition): Promise<boolean> => {
      if (!dialog) return false;
      return (await onTransition(dialog.procedure.id, data)) !== null;
    },
    [dialog, onTransition],
  );

  // Ohne den Schritt, der als Knopf daneben steht; oben der Ablauf, nach dem Strich der Rest.
  const button = menu ? mainAction(menu.procedure) : null;
  const visible = (menu?.procedure.actions ?? []).filter(
    (action) => action.action !== button?.action,
  );
  const flow = visible.filter((action) => !isSideAction(action));
  const side = visible.filter(isSideAction);
  const asItem = (action: ProcedureAction): ActionsMenuItem => ({
    label: action.label,
    icon: actionIcon(action),
    onClick: () => menu && openAction(menu.procedure, action),
  });
  const items: ActionsMenuItem[] = [
    ...flow.map(asItem),
    ...side.map((action, index) => ({
      ...asItem(action),
      dividerBefore: index === 0 && flow.length > 0,
    })),
  ];

  const elements = (
    <>
      <ActionsMenuList
        items={items}
        anchor={menu?.anchor ?? null}
        onClose={() => setMenu(null)}
        align="left"
      />
      <AntragUebergangDialog
        procedure={dialog?.procedure ?? null}
        action={dialog?.action ?? null}
        onClose={() => setDialog(null)}
        onSubmit={handleSubmit}
      />
    </>
  );

  return { openMenu, openAction, elements };
}
